import React, { useEffect, useState } from 'react';
import { LoginCredentials } from '../data/auth';
import { auth } from '../firebase/firebase-config';
import { User, getRedirectResult } from 'firebase/auth';
import { getGoogleAuthErrorMessage } from '../utils/google-auth-errors';
import { getApiUrl, isFirebaseHostedRuntime } from '../utils/api-url';
import './Login.css';
import { signInWithGoogle } from '../firebase/firebase-config';

interface LoginProps {
  onLoginSuccess: (userId: string, role: string, userName?: string) => void;
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
}

const Login: React.FC<LoginProps> = ({
  onLoginSuccess,
  onSwitchToRegister,
  onSwitchToForgotPassword,
}) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    emailOrPhone: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const completeFirebaseOnlyLogin = (user: User) => {
    const existingRole = localStorage.getItem('userRole');
    const fallbackRole = existingRole && existingRole !== 'guest' ? existingRole : 'professional';
    const fallbackName = user.displayName || user.email || 'User';

    localStorage.setItem('authToken', `firebase-${user.uid}`);
    localStorage.setItem('userId', user.uid);
    localStorage.setItem('userRole', fallbackRole);
    localStorage.setItem('userEmail', user.email || '');
    localStorage.setItem('userName', fallbackName);

    console.warn('⚠️ Backend auth unavailable. Signed in with Firebase-only fallback session.');
    onLoginSuccess(user.uid, fallbackRole, fallbackName);
  };

  const processGoogleUser = async (user: User) => {
    console.log('✅ Google Sign-In successful!');
    console.log('👤 User:', user.email);
    console.log('🆔 UID:', user.uid);

    if (isFirebaseHostedRuntime()) {
      console.warn('Using Firebase-hosted Google login fallback (no backend round-trip).');
      completeFirebaseOnlyLogin(user);
      return;
    }

    console.log('Sending request to backend...');
    try {
      const response = await fetch(getApiUrl('/auth/google-login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: user.displayName,
          googleId: user.uid,
          photoURL: user.photoURL,
        }),
      });

      console.log('Backend response status:', response.status);
      const data = await response.json();
      console.log('Backend response data:', data);

      if (data.ok) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userId', data.userId);
        localStorage.setItem('userRole', data.role);
        localStorage.setItem('userEmail', user.email || '');
        if (data.userName) {
          localStorage.setItem('userName', data.userName);
        }

        if (data.role === 'admin' || data.verified === true) {
          localStorage.setItem(`user_approval_${data.userId}`, 'verified');
        }

        console.log('Login successful, redirecting...');
        onLoginSuccess(data.userId, data.role, data.userName);
        return;
      }

      console.error('Backend returned error:', data.message);
      setError(data.message || 'Google sign-in failed. Please try again.');
    } catch (error: any) {
      const message = error?.message || '';
      if (message.includes('Failed to fetch')) {
        completeFirebaseOnlyLogin(user);
        return;
      }

      throw error;
    }
  };

  useEffect(() => {
    const stagedError = sessionStorage.getItem('googleAuthError');
    if (stagedError) {
      setError(stagedError);
      sessionStorage.removeItem('googleAuthError');
    }

    const activeGoogleFlow = sessionStorage.getItem('googleAuthFlow');
    if (activeGoogleFlow !== 'login') {
      return;
    }

    const handleRedirectSignIn = async () => {
      if (!auth) {
        return;
      }

      try {
        setGoogleLoading(true);
        const redirectResult = await getRedirectResult(auth);

        if (redirectResult?.user) {
          await processGoogleUser(redirectResult.user);
        } else {
          // User cancelled Google sign-in or closed popup
          setError(
            'Sign-in was cancelled. Please click “Continue with Google” and complete the sign-in in the popup window.'
          );
        }
      } catch (err) {
        console.error('❌ Redirect sign-in error:', err);
        setError(getGoogleAuthErrorMessage(err));
      } finally {
        setGoogleLoading(false);
        sessionStorage.removeItem('googleAuthFlow');
      }
    };

    handleRedirectSignIn();
  }, []);

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      if (!result || !result.user) {
        throw new Error('Google sign-in returned no user information.');
      }
      await processGoogleUser(result.user);
    } catch (err: any) {
      console.error('❌ Google sign-in error:', err);
      setError(getGoogleAuthErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(getApiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok && data?.ok) {
        // Store token in localStorage
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userId', data.userId);
        localStorage.setItem('userRole', data.role);
        localStorage.setItem('userEmail', credentials.emailOrPhone); // Store email for chat
        if (data.userName) {
          localStorage.setItem('userName', data.userName);
        }

        // Auto-verify admins and verified users
        if (data.role === 'admin' || data.verified === true) {
          localStorage.setItem(`user_approval_${data.userId}`, 'verified');
        }

        onLoginSuccess(data.userId, data.role, data.userName);
      } else {
        const message =
          data?.message ||
          `Login failed (${response.status}). Please verify your details and try again.`;
        setError(message);
      }
    } catch (err: any) {
      console.error('Login request error:', err);
      const message = err?.message || '';
      if (message.includes('Failed to fetch') || err?.name === 'TypeError') {
        setError('Unable to reach the server. Check your internet connection and try again.');
      } else {
        setError(message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Sign in to access your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="emailOrPhone">Email or Phone Number</label>
            <input
              id="emailOrPhone"
              type="text"
              value={credentials.emailOrPhone}
              onChange={(e) => setCredentials({ ...credentials, emailOrPhone: e.target.value })}
              placeholder="email@example.com or +27821234567"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              placeholder="Enter your password"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="login-button" disabled={loading || googleLoading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-divider">
          <span>OR</span>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="google-signin-button"
          disabled={loading || googleLoading}
        >
          <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {googleLoading ? 'Signing in with Google...' : 'Continue with Google'}
        </button>

        <div className="login-footer">
          <p>
            Don&apos;t have an account?{' '}
            <button onClick={onSwitchToRegister} className="link-button">
              Register here
            </button>
          </p>
          <button onClick={onSwitchToForgotPassword} className="forgot-password">
            Forgot password?
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
